import { useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, FileText } from "lucide-react";
import logger from "@/lib/logger";

function formatExportDate(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return dateStr || "";
  if (dateStr.includes("T")) {
    return new Date(dateStr).toLocaleString("en-IN");
  }
  return dateStr;
}

function sanitizeCsvField(value) {
  const str = String(value ?? "");
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function buildCsvContent(columns, entries) {
  const header = columns.map(c => sanitizeCsvField(c.label)).join(",");
  const rows = entries.map(entry =>
    columns.map(col => {
      const val = entry[col.key];
      const formatted = formatExportDate(val);
      return sanitizeCsvField(formatted);
    }).join(",")
  );
  return [header, ...rows].join("\n");
}

function downloadCsvBlob(csvContent, title) {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${title.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ExportButtons({ reportType, title, entries, columns, location }) {
  const handleCsvExport = useCallback(() => {
    if (!entries?.length) { toast.error("No data to export"); return; }
    try {
      const csvContent = buildCsvContent(columns, entries);
      downloadCsvBlob(csvContent, title);
      toast.success("CSV downloaded");
    } catch (err) { logger.error("CSV export failed:", err); toast.error("Export failed"); }
  }, [entries, columns, title]);

  const handlePdfExport = useCallback(async () => {
    if (!entries?.length) { toast.error("No data to export"); return; }
    try {
      const { generateFGATPdf } = await import("@/lib/pdfGenerator");
      await generateFGATPdf(reportType, entries, columns, location);
    } catch (err) { logger.error("PDF export failed:", err); toast.error("PDF export failed"); }
  }, [entries, columns, reportType, location]);

  return (
    <div className="flex gap-2">
      <Button variant="outline" size="sm" onClick={handleCsvExport} className="h-8 text-xs" data-testid="export-csv-btn">
        <FileSpreadsheet size={14} className="mr-1" /> CSV
      </Button>
      <Button variant="outline" size="sm" onClick={handlePdfExport} className="h-8 text-xs" data-testid="export-pdf-btn">
        <FileText size={14} className="mr-1" /> PDF
      </Button>
    </div>
  );
}
